/**
 * Shop The Look Custom Element
 * Hydrates the carousel inside this block with Preact `ResultsCard`s built
 * from product data emitted by Liquid, so we don't have to re-author the card
 * markup that already lives in `ResultCard.js`.
 *
 * Expected children:
 *   1. <script type="application/json" data-shop-the-look-products>[...]</script>
 *      Athos-shaped result objects that `ResultsCard` already consumes.
 *
 *   2. <template data-shop-the-look-template>
 *        <div ...>
 *          <x-carousel ... data-item-class="..." data-card-options='{...}'></x-carousel>
 *        </div>
 *      </template>
 *
 *      The `<x-carousel>` lives inside an inert <template> so its
 *      `connectedCallback` doesn't fire on an empty cell list. We render the
 *      Preact cards into it while still detached, then move the fragment into
 *      the DOM so Flickity initialises once with cells already in place.
 */

import { Fragment, h, render } from 'preact'
import { ResultsCard } from '../libs/athos/components/ResultCard'
import { parseJSON } from '../helpers/utils'

interface CardOptions {
  hideSwatches: boolean
  hidePromolines: boolean
  hideBadges: boolean
}

const DEFAULT_CARD_OPTIONS: CardOptions = {
  hideSwatches: false,
  hidePromolines: false,
  hideBadges: false
}

type ResultLike = Record<string, unknown> & { handle?: string }

type CarouselHost = HTMLElement & { flickity?: { resize: () => void } }

export class ShopTheLookElement extends HTMLElement {
  private hydrated = false
  private mountedTarget: Element | null = null
  private ratingObserver: MutationObserver | null = null

  connectedCallback() {
    if (this.hydrated) return

    if (this.canHydrate()) {
      this.hydrate()
    } else {
      requestAnimationFrame(() => this.connectedCallback())
    }
  }

  disconnectedCallback() {
    this.ratingObserver?.disconnect()
    this.ratingObserver = null

    if (this.mountedTarget) {
      render(null, this.mountedTarget)
      this.mountedTarget = null
    }
  }

  private canHydrate(): boolean {
    return Boolean(
      this.querySelector('script[data-shop-the-look-products]') &&
      this.querySelector('template[data-shop-the-look-template]')
    )
  }

  private hydrate() {
    if (this.hydrated) return
    this.hydrated = true

    const script = this.querySelector<HTMLScriptElement>('script[data-shop-the-look-products]')
    const template = this.querySelector<HTMLTemplateElement>('template[data-shop-the-look-template]')
    if (!script || !template) return

    const products = parseJSON(script.textContent || '[]', 'array') as ResultLike[]
    if (!Array.isArray(products) || products.length === 0) return

    const fragment = template.content.cloneNode(true) as DocumentFragment
    const xCarousel = fragment.querySelector('x-carousel') as HTMLElement | null
    if (!xCarousel) return

    const itemClass = xCarousel.dataset.itemClass || ''
    const cardOptions: CardOptions = {
      ...DEFAULT_CARD_OPTIONS,
      ...(parseJSON(xCarousel.dataset.cardOptions || '{}') as Partial<CardOptions>)
    }

    const cards = products.map((product, index) =>
      h(
        'div',
        {
          class: `${itemClass} shrink-0`.trim(),
          key: (product.handle as string) || index
        },
        h(ResultsCard as never, {
          result: product,
          dataIndex: String(index),
          options: cardOptions
        })
      )
    )

    render(h(Fragment, null, cards), xCarousel)

    this.mountedTarget = xCarousel
    template.replaceWith(fragment)

    const carousel = this.querySelector('x-carousel') as CarouselHost | null
    if (carousel) {
      this.watchCarouselHeight(carousel)
    }
  }

  private watchCarouselHeight(carousel: CarouselHost) {
    this.ratingObserver?.disconnect()

    const resize = () => carousel.flickity?.resize()

    this.ratingObserver = new MutationObserver(() => {
      if (this.querySelector('.kl_reviews__stars_badge')) {
        resize()
      }
    })

    this.ratingObserver.observe(this, { subtree: true, childList: true })
    setTimeout(() => {
      this.ratingObserver?.disconnect()
      this.ratingObserver = null
    }, 10000)
  }
}
